/**
 * The cp feature rights (four run/agent rights plus cp_agentStatement) and the ONE place that turns
 * a server refusal into a demotion.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * DOCUMENTED DEVIATION FROM 04-frontend.md §5.1 (orchestrator decision OD-1)
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * 04 §5.1 asks for "Alle Buttons nur mit um-Recht aktiv" — buttons active ONLY with the um right.
 * Taken literally that requires the frontend to KNOW the grant before rendering. It cannot:
 *
 *   1. There is NO feature-rights service in this frontend. A repo-wide search for
 *      hasRight / hasPermission / featureRight / hasFeature / moduleFeature across
 *      `features/core/shared` returns nothing usable; the um RoleAuth artefacts that do exist are
 *      pure configuration DTOs for the admin UI, and their entries in `project.dto.service.map.ts`
 *      are commented out.
 *   2. No cp backend payload carries a grant flag either — there is no `runExecutionAllowed`,
 *      `agentChangeAllowed` or sibling field anywhere in `com.mvs.backend.cp`.
 *
 * So a literal "default false" is NOT a safe default here: nothing would ever raise it, and the
 * button would be permanently inert. That is not security, it is a lost function — the caseworker
 * loses the action and gets no path to it, while an attacker gains nothing, because the frontend
 * was never the boundary.
 *
 * THE BOUNDARY IS THE BACKEND. `cp/cross/um/ProvisionFeatureGuard` evaluates every one of these
 * four rights through `RolesRuntime.getModuleFeatureAccess("cp", feature)` and throws
 * `CoreAuthorizationRuntimeException` -> HTTP 403 with a German message. Note its polarity: the
 * guard is DEFAULT-ALLOW and refuses only on an explicitly seeded `deny` (the `ch.cross.um`
 * precedent). An optimistic frontend therefore MATCHES the server's own default; a pessimistic one
 * would contradict it and hide an action the server would have permitted.
 *
 * THE MODULE PATTERN, consequently, is the same for all four rights:
 *   * the flag starts GRANTED (optimistic) — the button is live and the action reachable;
 *   * a 403 from the action endpoint is the right answering the hard way: the flag is DEMOTED,
 *     stickily for that component instance, and the user is told in German why;
 *   * a host that DOES know the grant (or a backend that later ships one on its payload) still
 *     wins over the default — the optimistic value is the fallback, not an override.
 *
 * When a feature-rights service finally lands, exactly one thing changes: the components read
 * their initial value from it instead of defaulting to true. The demotion path below stays as is,
 * because a 403 is still the authoritative answer.
 *
 * German UI text with real umlauts, English code and comments (module convention).
 */

/** Key of one cp feature right — mirrors the constants of `cp/cross/um/ProvisionFeatureGuard`. */
export type CpFeatureRightKey =
    'cp_runExecution'
    | 'cp_manualBooking'
    | 'cp_reserveTransfer'
    | 'cp_agentChange'
    | 'cp_agentStatement';

/**
 * Static helper — deliberately NOT an `@Injectable` service: it holds no state, needs no DI, and
 * is used from components that are constructed by the widget/OBC machinery. Making it injectable
 * would force a provider into every host for a pure function.
 */
export class CpFeatureRight {

    /** Drive a `ProvisionRun` along its status chain (BR-017…BR-020, U-09). */
    static readonly RUN_EXECUTION: CpFeatureRightKey = 'cp_runExecution';

    /** Create a manual booking on an agent account (BR-062). */
    static readonly MANUAL_BOOKING: CpFeatureRightKey = 'cp_manualBooking';

    /** Move money between the reserve and the provision account of one agent (U-03/KF-08). */
    static readonly RESERVE_TRANSFER: CpFeatureRightKey = 'cp_reserveTransfer';

    /** Reassign the provision agent of a `ProvisionContract` (BR-016). */
    static readonly AGENT_CHANGE: CpFeatureRightKey = 'cp_agentChange';

    /** Issue (job over all agents of a run) and re-issue the final agent statements (Provisionsabrechnung). */
    static readonly AGENT_STATEMENT: CpFeatureRightKey = 'cp_agentStatement';

    /** Header of every refusal the module shows — one wording across all four rights. */
    static readonly DENIED_HEADER: string = 'Keine Berechtigung';

    /**
     * The German refusal texts, one per right. They live here rather than in the components so the
     * four actions cannot drift into four different ways of saying the same thing, and so the
     * wording stays parallel to the German messages `ProvisionFeatureGuard` itself throws.
     */
    private static readonly DENIED_MESSAGES: { [key in CpFeatureRightKey]: string } = {
        'cp_runExecution':
            'Ihnen fehlt die Berechtigung, Provisionsläufe zu steuern (cp_runExecution).',
        'cp_manualBooking':
            'Ihnen fehlt die Berechtigung, manuelle Buchungen zu erfassen (cp_manualBooking).',
        'cp_reserveTransfer':
            'Ihnen fehlt die Berechtigung, zwischen Rücklagen- und Provisionskonto umzubuchen '
            + '(cp_reserveTransfer).',
        'cp_agentChange':
            'Ihnen fehlt die Berechtigung, den Berater zu wechseln (cp_agentChange).',
        'cp_agentStatement':
            'Ihnen fehlt die Berechtigung, Provisionsabrechnungen zu erstellen oder neu auszustellen '
            + '(cp_agentStatement).'
    };

    /**
     * Is this error the feature right refusing?
     *
     * ONLY 403 counts. A 401 is an expired session (the auth interceptor handles it), and a 500
     * is a defect — demoting the flag on either would silently disable an action the user still
     * holds, and nothing in the UI would ever raise it again.
     */
    static isForbidden(error: any): boolean {
        return error?.status === 403;
    }

    /** The German message shown when {@link isForbidden} answered true for `right`. */
    static deniedMessage(right: CpFeatureRightKey): string {
        return CpFeatureRight.DENIED_MESSAGES[right];
    }

    /**
     * Resolve the effective grant of a right for one component instance.
     *
     * Order of authority, highest first:
     *   1. a server 403 already seen — sticky, a refusal does not soften on the next read;
     *   2. what the host explicitly knows (`true`/`false`), if it knows anything;
     *   3. what a backend payload carried, if it carried anything;
     *   4. the optimistic default — GRANTED (see the class header for why).
     *
     * `null`/`undefined` at levels 2 and 3 mean "no answer", NOT "denied" — that distinction is
     * the whole point, because "no answer" is the normal case until a rights service exists.
     */
    static resolve(
        deniedByServer: boolean,
        hostAnswer: boolean | null | undefined,
        payloadAnswer: boolean | null | undefined): boolean {

        if (deniedByServer) {
            return false;
        }

        if (hostAnswer != null) {
            return hostAnswer;
        }

        if (payloadAnswer != null) {
            return payloadAnswer;
        }

        return true;
    }
}
